import type { ColumnSizingState } from '@tanstack/react-table';

import { DEFAULT_COLUMN_SIZE } from '../constants';
import type { ColumnConfig } from '../types';

/**
 * Calculates enhanced column sizes to fit container width.
 * Ported from data-table-old/use-data-table.ts calculateEnhancedSizes.
 *
 * Key behaviors:
 * - If a column has growFirst=true, that column grows/shrinks to absorb the delta
 * - Otherwise, the delta is distributed equally across flexible columns
 * - Flexible columns are bounded below by their minSize and above by their maxSize
 * - Returns only enhanced sizes (not a complete map) to allow TanStack defaults to work
 * - Respects user-resized columns by using currentSizes as base
 */
export function calculateEnhancedSizes<TData>(
  columns: ColumnConfig<TData>[],
  containerWidth: number,
  currentSizes: ColumnSizingState,
  defaultColumnSize: number = DEFAULT_COLUMN_SIZE
): ColumnSizingState {
  // If container not measured yet, return current sizes
  if (containerWidth <= 0) {
    return currentSizes;
  }

  // Calculate total table width from all columns
  const currentTableWidth = columns.reduce(
    (sum, col) => sum + (currentSizes[col.id] ?? col.size ?? defaultColumnSize),
    0
  );

  // Sub-pixel tolerance: distributing delta across N columns rarely sums back
  // exactly (e.g. 7/3 * 3 = 7.000000000000001), so === would miss the steady state.
  if (Math.abs(currentTableWidth - containerWidth) < 1) {
    return currentSizes;
  }

  const delta = containerWidth - currentTableWidth;

  // Check if any column explicitly has growFirst set
  const growColumn = columns.find((col) => col.growFirst);

  if (growColumn) {
    // Grow/shrink only the designated column to absorb the delta
    const currentGrowSize = currentSizes[growColumn.id] ?? growColumn.size ?? defaultColumnSize;
    const growColumnWidth = Math.max(
      growColumn.minSize ?? defaultColumnSize,
      currentGrowSize + delta
    );

    return {
      ...currentSizes,
      [growColumn.id]: growColumnWidth,
    };
  }

  // No growFirst specified - distribute delta equally across flexible columns
  // Columns with explicit sizes are fixed; only columns without explicit sizes flex
  const flexibleColumns = columns.filter((col) => !col.size);
  const fixedColumns = columns.filter((col) => col.size);

  // If all columns have explicit sizes, no distribution possible
  if (flexibleColumns.length === 0) {
    return currentSizes;
  }

  const deltaPerFlexColumn = delta / flexibleColumns.length;
  const newSizes: ColumnSizingState = { ...currentSizes };

  // Fixed columns keep their exact size
  fixedColumns.forEach((col) => {
    newSizes[col.id] = currentSizes[col.id] ?? col.size!;
  });

  // Flexible columns get the delta distributed equally, clamped to min/max
  flexibleColumns.forEach((col) => {
    const baseSize = currentSizes[col.id] ?? defaultColumnSize;
    const newSize = baseSize + deltaPerFlexColumn;
    const minSize = col.minSize ?? defaultColumnSize;
    const maxSize = col.maxSize ?? Infinity;
    newSizes[col.id] = Math.min(maxSize, Math.max(minSize, newSize));
  });

  return newSizes;
}
